/**
 * Lachsöl-only gallery enhancement.
 *
 * This file is loaded exclusively on the lachsol product page (see the
 * `selected_product.handle == 'lachsol'` guard in
 * snippets/product-media-gallery-content.liquid that emits the <script> tag
 * referencing this asset). It does not run on any other product page.
 *
 * It does not touch variant selection, price, unit price, cart state, or the
 * hidden variant id field. It only decides which slide of the *existing*
 * media gallery is visible, using the theme's own Slideshow#select() API.
 *
 * Behaviour:
 *  1. On a normal canonical page load (no ?variant= or ?option_values= in the
 *     URL), the gallery still natively selects the 100 ml variant and its
 *     media server-side exactly as before. Once, after the gallery component
 *     has fully initialized, this script moves the visible slide to the
 *     product's hero media (both bottles) - identified by its real Shopify
 *     media id, not by position. This happens only once per full page load;
 *     it is not re-run after client-side section re-fetches (variant
 *     switches replace the <media-gallery> element wholesale, and this
 *     script's one-time guard lives outside that element).
 *  2. If the URL already specifies an explicit ?variant= or ?option_values=
 *     parameter, the hero override is skipped entirely so the deep-linked
 *     variant's own media is respected.
 *  3. Because the 100 ml option is already selected on load, clicking it
 *     again does not fire a native `change` event, so the existing
 *     variant-picker component (assets/variant-picker.js) never reacts to
 *     that click. This script adds a narrowly scoped click/keydown handler,
 *     limited to <variant-picker data-product-handle="lachsol"> radio
 *     inputs, that re-activates the currently selected variant's own media
 *     (again by real media id) when its already-checked option is
 *     re-clicked or re-activated via keyboard. It never changes selection
 *     state, price, unit price, the variant id field, or cart state.
 */
(() => {
  const HERO_INIT_FLAG = '__lachsolHeroMediaInitialized__';

  /**
   * Finds the index of the slide whose media matches the given media id.
   * @param {import('./media-gallery').MediaGallery} gallery
   * @param {string} mediaId
   * @returns {number}
   */
  function findSlideIndexByMediaId(gallery, mediaId) {
    const slides = gallery.slideshow?.refs.slides;
    if (!slides || !mediaId) return -1;

    return slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`) != null);
  }

  /**
   * Activates a slide by media id, without touching variant/price/cart state.
   * @param {import('./media-gallery').MediaGallery} gallery
   * @param {string | undefined} mediaId
   */
  function activateMediaById(gallery, mediaId) {
    if (!gallery.slideshow || !mediaId) return;

    const index = findSlideIndexByMediaId(gallery, mediaId);
    if (index > -1) {
      gallery.slideshow.select(index, undefined, { animate: false });
    }
  }

  function hasExplicitVariantInUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.has('variant') || params.has('option_values');
  }

  async function initHeroOverride() {
    const gallery = document.querySelector('media-gallery[data-lachsol-hero-media-id]');
    if (!gallery) return;

    // Respect explicit variant deep links: do not override with the hero image.
    if (hasExplicitVariantInUrl()) return;

    // One-time guard: never re-run after client-side section re-fetches.
    if (window[HERO_INIT_FLAG]) return;
    window[HERO_INIT_FLAG] = true;

    await Promise.all([customElements.whenDefined('media-gallery'), customElements.whenDefined('slideshow-component')]);

    activateMediaById(gallery, gallery.dataset.lachsolHeroMediaId);
  }

  /**
   * Handles re-activation of the already-selected size option, restoring its
   * own (non-hero) media without changing selection, price, or cart state.
   */
  function initReselectHandler() {
    const radioSelector = 'variant-picker[data-product-handle="lachsol"] fieldset input[type="radio"]';

    /** @type {boolean | null} */
    let wasCheckedBeforeActivation = null;

    /** @param {Event} event */
    function captureBeforeState(event) {
      if (!(event.target instanceof Element)) return;
      const input = event.target.closest(radioSelector);
      if (!(input instanceof HTMLInputElement)) return;

      wasCheckedBeforeActivation = input.dataset.currentChecked === 'true';
    }

    // Capture state before the browser applies the click/keyboard default action.
    document.addEventListener('pointerdown', captureBeforeState, { capture: true });
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== ' ' && event.key !== 'Spacebar') return;
        captureBeforeState(event);
      },
      { capture: true }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        const input = event.target.closest(radioSelector);
        if (!(input instanceof HTMLInputElement)) return;

        const wasAlreadySelected = wasCheckedBeforeActivation === true;
        wasCheckedBeforeActivation = null;

        if (!wasAlreadySelected) return;

        const gallery = document.querySelector('media-gallery[data-lachsol-selected-media-id]');
        if (!gallery) return;

        activateMediaById(gallery, gallery.dataset.lachsolSelectedMediaId);
      },
      { capture: true }
    );
  }

  initHeroOverride();
  initReselectHandler();
})();
